import { getRowLabel, type AnchoredRow, type UnitMapping } from '../types/anchoredRowTypes'
import type { LedgerSafeUnassignedFragment } from './ledgerSafeBoundaryReconcile'
import { getCollationRowTitle } from './ledgerSafeRowTitles'

export type LedgerSafeCellState = 'mapped' | 'gap' | 'excluded'

export interface LedgerSafeSelectedCell {
  rowId: string
  witnessId: string
}

export interface LedgerSafeRowEditResult {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  selectedCell: LedgerSafeSelectedCell
  selectedRowIds?: string[]
  // Requests sequential display-label derivation after the edit. It does not
  // make label-only changes to neighboring rows durable database writes.
  requiresRelabel: boolean
}

const buildRowPersistenceSignature = (row: AnchoredRow): string => JSON.stringify({
  id: row.id,
  chapterId: row.chapterId,
  labelPrefix: row.labelPrefix,
  labelNumber: row.labelNumber,
  position: row.position,
  unitMappings: row.unitMappings,
  createdAt: row.createdAt,
  createdBy: row.createdBy || null,
  status: row.status,
  metadata: row.metadata || null,
})

export const selectLedgerSafeRowEditUpserts = ({
  previousRows,
  result,
}: {
  previousRows: AnchoredRow[]
  result: LedgerSafeRowEditResult
}): AnchoredRow[] => {
  const changedRowIds = Array.from(new Set(result.changedRowIds))
  if (changedRowIds.length === 0) {
    throw new Error('Row edit did not declare any changed rows')
  }

  const changedRowIdSet = new Set(changedRowIds)
  const previousById = new Map(previousRows.map(row => [row.id, row]))
  const nextById = new Map(result.updatedRows.map(row => [row.id, row]))
  if (nextById.size !== result.updatedRows.length) {
    throw new Error('Row edit produced duplicate row identities')
  }

  const removedRowId = previousRows.find(row => !nextById.has(row.id))?.id
  if (removedRowId) {
    throw new Error(`Row edit cannot remove ${removedRowId} through the non-destructive upsert path`)
  }

  const missingChangedRowId = changedRowIds.find(rowId => !nextById.has(rowId))
  if (missingChangedRowId) {
    throw new Error(`Row edit declared missing row ${missingChangedRowId}`)
  }

  const undeclaredChangedRow = result.updatedRows.find(nextRow => {
    if (changedRowIdSet.has(nextRow.id)) {
      return false
    }
    const previousRow = previousById.get(nextRow.id)
    if (!previousRow) {
      return true
    }
    return previousRow !== nextRow
      && buildRowPersistenceSignature(previousRow) !== buildRowPersistenceSignature(nextRow)
  })
  if (undeclaredChangedRow) {
    throw new Error(`Row edit did not declare changed row ${undeclaredChangedRow.id}`)
  }

  return result.updatedRows.filter(row => changedRowIdSet.has(row.id))
}

export interface LedgerSafeEmptyRowRun {
  rowIds: string[]
  rowCount: number
  firstRowLabel: string
  lastRowLabel: string
  label: string
  previousRowId: string | null
  previousRowLabel: string | null
  nextRowId: string | null
  nextRowLabel: string | null
}

export type LedgerSafeFragmentAutoPlacementMode =
  | 'place-in-previous'
  | 'place-in-next'
  | 'insert-after-previous'
  | 'insert-before-next'

export interface LedgerSafeFragmentAutoPlacementAction {
  fragmentUnitUuid: string
  mode: LedgerSafeFragmentAutoPlacementMode
  rowId?: string
  anchorRowId?: string
  changedRowIds: string[]
}

export interface LedgerSafeFragmentAutoPlacementSkip {
  fragmentUnitUuid: string
  reason: string
}

export interface LedgerSafeFragmentAutoPlacementResult extends LedgerSafeRowEditResult {
  placedFragments: LedgerSafeFragmentAutoPlacementAction[]
  skippedFragments: LedgerSafeFragmentAutoPlacementSkip[]
}

const nowIso = (): string => new Date().toISOString()

const generateRowId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ledger-row-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  return left.labelNumber - right.labelNumber
})

const getMappingsForWitness = (row: AnchoredRow, witnessId: string): UnitMapping[] =>
  row.unitMappings.filter(mapping => mapping.witnessId === witnessId)

export const isLedgerSafeRowTrulyEmpty = (row: AnchoredRow): boolean =>
  (!Array.isArray(row.unitMappings) || row.unitMappings.length === 0)
  && !getCollationRowTitle(row)
  && !row.metadata?.scene

export const summarizeLedgerSafeEmptyRowRuns = (rows: AnchoredRow[]): LedgerSafeEmptyRowRun[] => {
  const sortedRows = sortRows(rows)
  const runs: LedgerSafeEmptyRowRun[] = []

  let index = 0
  while (index < sortedRows.length) {
    const row = sortedRows[index]
    if (!row || !isLedgerSafeRowTrulyEmpty(row)) {
      index += 1
      continue
    }

    const runStartIndex = index
    let runEndIndex = index
    while (runEndIndex + 1 < sortedRows.length && isLedgerSafeRowTrulyEmpty(sortedRows[runEndIndex + 1])) {
      runEndIndex += 1
    }

    const runRows = sortedRows.slice(runStartIndex, runEndIndex + 1)
    const firstRow = runRows[0]
    const lastRow = runRows[runRows.length - 1]
    const previousRow = runStartIndex > 0 ? sortedRows[runStartIndex - 1] : null
    const nextRow = runEndIndex + 1 < sortedRows.length ? sortedRows[runEndIndex + 1] : null
    const firstRowLabel = getRowLabel(firstRow)
    const lastRowLabel = getRowLabel(lastRow)

    runs.push({
      rowIds: runRows.map(candidate => candidate.id),
      rowCount: runRows.length,
      firstRowLabel,
      lastRowLabel,
      label: runRows.length === 1 ? firstRowLabel : `${firstRowLabel}–${lastRowLabel}`,
      previousRowId: previousRow?.id || null,
      previousRowLabel: previousRow ? getRowLabel(previousRow) : null,
      nextRowId: nextRow?.id || null,
      nextRowLabel: nextRow ? getRowLabel(nextRow) : null
    })

    index = runEndIndex + 1
  }

  return runs
}

export const getLedgerSafeCellState = (row: AnchoredRow, witnessId: string): LedgerSafeCellState => {
  const mappings = getMappingsForWitness(row, witnessId)
  if (mappings.some(mapping => !mapping.excluded)) {
    return 'mapped'
  }
  if (mappings.some(mapping => mapping.excluded)) {
    return 'excluded'
  }
  return 'gap'
}

const getActiveMapping = (row: AnchoredRow, witnessId: string): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)

const getExcludedMapping = (row: AnchoredRow, witnessId: string): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && mapping.excluded)

const replaceWitnessMappings = (
  row: AnchoredRow,
  witnessId: string,
  nextMappings: UnitMapping[]
): AnchoredRow => {
  const timestamp = nowIso()
  return {
    ...row,
    unitMappings: [
      ...row.unitMappings.filter(mapping => mapping.witnessId !== witnessId),
      ...nextMappings
    ],
    status: row.status === 'confirmed' ? 'needs-review' : row.status,
    updatedAt: timestamp
  }
}

const buildMappingFromFragment = (fragment: LedgerSafeUnassignedFragment): UnitMapping => ({
  witnessId: fragment.witnessId,
  unitUuid: fragment.unitUuid,
  unitLabel: fragment.unitLabel,
  textSnapshot: fragment.text,
  excluded: false,
  addedAt: nowIso()
})

const removeDuplicateWitnessUnit = (
  row: AnchoredRow,
  witnessId: string,
  unitUuid: string
): AnchoredRow => {
  const hasDuplicate = row.unitMappings.some(
    mapping => mapping.witnessId === witnessId && mapping.unitUuid === unitUuid
  )
  if (!hasDuplicate) {
    return row
  }
  return replaceWitnessMappings(
    row,
    witnessId,
    row.unitMappings.filter(
      mapping => mapping.witnessId === witnessId && mapping.unitUuid !== unitUuid
    )
  )
}

const computeInsertedPosition = (
  rows: AnchoredRow[],
  sourceIndex: number,
  placement: 'above' | 'below'
): number => {
  const source = rows[sourceIndex]
  if (!source) {
    throw new Error('Source row not found')
  }
  if (placement === 'above') {
    const previous = rows[sourceIndex - 1]
    if (!previous) {
      return source.position - 1
    }
    return previous.position + ((source.position - previous.position) / 2)
  }
  const next = rows[sourceIndex + 1]
  if (!next) {
    return source.position + 1
  }
  return source.position + ((next.position - source.position) / 2)
}

const cloneMapping = (mapping: UnitMapping): UnitMapping => ({
  ...mapping,
  excluded: false,
  addedAt: mapping.addedAt || nowIso()
})

const getContiguousSelectionIndexes = (
  rows: AnchoredRow[],
  rowIds: string[]
): number[] => {
  const uniqueRowIds = Array.from(new Set(rowIds))
  if (uniqueRowIds.length < 2) {
    throw new Error('Select at least two rows to shift a range')
  }

  const indexes = uniqueRowIds.map(rowId => rows.findIndex(row => row.id === rowId))
  if (indexes.some(index => index < 0)) {
    throw new Error('One or more selected rows were not found')
  }

  const sortedIndexes = [...indexes].sort((left, right) => left - right)
  for (let offset = 1; offset < sortedIndexes.length; offset += 1) {
    if (sortedIndexes[offset] !== sortedIndexes[0] + offset) {
      throw new Error('Range shift requires a contiguous row selection')
    }
  }
  return sortedIndexes
}

export const compactLedgerSafeEmptyRows = ({
  rows,
  selectedWitnessId,
  rowIds
}: {
  rows: AnchoredRow[]
  selectedWitnessId: string
  rowIds?: string[]
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const targetRowIds = rowIds ? new Set(rowIds) : null
  const emptyRows = sortedRows.filter(row =>
    isLedgerSafeRowTrulyEmpty(row) && (!targetRowIds || targetRowIds.has(row.id))
  )

  if (emptyRows.length === 0) {
    throw new Error('No truly empty rows to compact')
  }
  if (emptyRows.length === sortedRows.length) {
    throw new Error('Cannot compact every row in the collation')
  }

  const emptyRowIds = new Set(emptyRows.map(row => row.id))
  const updatedRows = sortedRows.filter(row => !emptyRowIds.has(row.id))
  const firstDeletedIndex = sortedRows.findIndex(row => emptyRowIds.has(row.id))
  const selectedRow = updatedRows[Math.min(firstDeletedIndex, updatedRows.length - 1)]

  return {
    updatedRows,
    changedRowIds: emptyRows.map(row => row.id),
    selectedCell: {
      rowId: selectedRow.id,
      witnessId: selectedWitnessId
    },
    requiresRelabel: true
  }
}

export const applyLedgerSafeRelocateRange = ({
  rows,
  witnessId,
  sourceRowIds,
  targetStartRowId,
  expectedUnitUuids
}: {
  rows: AnchoredRow[]
  witnessId: string
  sourceRowIds: string[]
  targetStartRowId: string
  expectedUnitUuids?: string[]
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const uniqueSourceRowIds = Array.from(new Set(sourceRowIds))
  if (uniqueSourceRowIds.length === 0) {
    throw new Error('Select at least one mapped cell to relocate')
  }

  const sourceIndexes = uniqueSourceRowIds
    .map(rowId => sortedRows.findIndex(row => row.id === rowId))
    .sort((left, right) => left - right)
  if (sourceIndexes.some(index => index < 0)) {
    throw new Error('One or more selected rows were not found')
  }
  for (let offset = 1; offset < sourceIndexes.length; offset += 1) {
    if (sourceIndexes[offset] !== sourceIndexes[0] + offset) {
      throw new Error('Relocation requires a contiguous row selection')
    }
  }

  const targetStartIndex = sortedRows.findIndex(row => row.id === targetStartRowId)
  if (targetStartIndex < 0) {
    throw new Error('Selected destination row was not found')
  }
  const targetEndIndex = targetStartIndex + sourceIndexes.length - 1
  if (targetEndIndex >= sortedRows.length) {
    throw new Error('Relocation range extends past the final chapter row')
  }
  if (targetStartIndex === sourceIndexes[0]) {
    throw new Error('Selected cells are already in that location')
  }

  const orderedSourceRows = sourceIndexes.map(index => sortedRows[index])
  const sourceMappings = orderedSourceRows.map(row => {
    const activeMappings = getMappingsForWitness(row, witnessId).filter(mapping => !mapping.excluded)
    if (activeMappings.length === 0) {
      throw new Error('Relocation requires mapped units in every selected row')
    }
    if (activeMappings.length > 1) {
      throw new Error('Relocation blocked because a selected cell contains multiple mapped units')
    }
    return activeMappings[0]
  })
  const sourceUnitUuidSet = new Set(sourceMappings.map(mapping => mapping.unitUuid))
  if (sourceUnitUuidSet.size !== sourceMappings.length) {
    throw new Error('Relocation blocked because the selected cells repeat a unit identity')
  }
  if (
    expectedUnitUuids
    && (
      expectedUnitUuids.length !== sourceMappings.length
      || sourceMappings.some((mapping, index) => mapping.unitUuid !== expectedUnitUuids[index])
    )
  ) {
    throw new Error('Selected units changed after relocation mode began')
  }

  const sourceRowIdSet = new Set(orderedSourceRows.map(row => row.id))
  const targetRows = sortedRows.slice(targetStartIndex, targetEndIndex + 1)
  targetRows.forEach(row => {
    if (getActiveMapping(row, witnessId) && !sourceRowIdSet.has(row.id)) {
      throw new Error('Target row already has a mapped unit for this witness')
    }
  })

  const replacementByRowId = new Map<string, UnitMapping[]>()
  orderedSourceRows.forEach(row => replacementByRowId.set(row.id, []))
  targetRows.forEach((row, index) => {
    replacementByRowId.set(row.id, [cloneMapping(sourceMappings[index])])
  })

  const movedUnitIds = new Set(sourceMappings.map(mapping => mapping.unitUuid))
  const changedRowIdSet = new Set([
    ...orderedSourceRows.map(row => row.id),
    ...targetRows.map(row => row.id)
  ])
  const updatedRows = sortedRows.map(row => {
    let nextRow = row
    movedUnitIds.forEach(unitUuid => {
      nextRow = removeDuplicateWitnessUnit(nextRow, witnessId, unitUuid)
    })
    if (nextRow !== row) {
      changedRowIdSet.add(row.id)
    }
    const replacement = replacementByRowId.get(row.id)
    return replacement ? replaceWitnessMappings(nextRow, witnessId, replacement) : nextRow
  })

  return {
    updatedRows,
    changedRowIds: sortedRows.filter(row => changedRowIdSet.has(row.id)).map(row => row.id),
    selectedCell: {
      rowId: targetRows[0].id,
      witnessId
    },
    selectedRowIds: targetRows.map(row => row.id),
    requiresRelabel: false
  }
}

export const applyLedgerSafeMoveMapping = ({
  rows,
  witnessId,
  rowId,
  direction
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
  direction: 'up' | 'down'
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const sourceIndex = sortedRows.findIndex(row => row.id === rowId)
  if (sourceIndex < 0) {
    throw new Error('Selected row was not found')
  }
  const targetIndex = direction === 'up' ? sourceIndex - 1 : sourceIndex + 1
  if (targetIndex < 0 || targetIndex >= sortedRows.length) {
    throw new Error(`Cannot move ${direction} from the edge row`)
  }

  const result = applyLedgerSafeRelocateRange({
    rows: sortedRows,
    witnessId,
    sourceRowIds: [rowId],
    targetStartRowId: sortedRows[targetIndex].id
  })

  return {
    ...result,
    selectedRowIds: undefined
  }
}

export const applyLedgerSafeMarkTransposition = ({
  rows,
  witnessId,
  originRowId,
  targetRowId
}: {
  rows: AnchoredRow[]
  witnessId: string
  originRowId: string
  targetRowId: string
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const originRow = sortedRows.find(row => row.id === originRowId)
  const targetRow = sortedRows.find(row => row.id === targetRowId)
  if (!originRow) {
    throw new Error('Selected source row was not found')
  }
  if (!targetRow) {
    throw new Error('Selected alignment row was not found')
  }
  if (originRow.id === targetRow.id) {
    throw new Error('A transposition requires a distinct target row')
  }

  const originMapping = getActiveMapping(originRow, witnessId)
  if (!originMapping) {
    throw new Error('Selected cell has no mapped unit to mark as transposed')
  }

  const targetMapping = getActiveMapping(targetRow, witnessId)
  if (targetMapping && targetRow.id !== originRow.id) {
    throw new Error('Target row already has a mapped unit for this witness')
  }

  const markedMapping: UnitMapping = {
    ...originMapping,
    translationStatus: 'transposed',
    excluded: false,
    addedAt: originMapping.addedAt || nowIso()
  }
  delete markedMapping.transpositionOriginRowId
  const changedRowIds = Array.from(new Set([originRow.id, targetRow.id]))
  const updatedRows = sortedRows.map(row => {
    let nextRow = removeDuplicateWitnessUnit(row, witnessId, markedMapping.unitUuid)
    if (row.id === originRow.id && originRow.id !== targetRow.id) {
      nextRow = replaceWitnessMappings(nextRow, witnessId, [])
    }
    if (row.id === targetRow.id) {
      nextRow = replaceWitnessMappings(nextRow, witnessId, [markedMapping])
    }
    return nextRow
  })

  return {
    updatedRows,
    changedRowIds,
    selectedCell: {
      rowId: targetRow.id,
      witnessId
    },
    requiresRelabel: false
  }
}

export const applyLedgerSafeDetachMappings = ({
  rows,
  witnessId,
  rowIds
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowIds: string[]
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const uniqueRowIds = Array.from(new Set(rowIds))
  if (uniqueRowIds.length === 0) {
    throw new Error('Select at least one row to detach')
  }
  const rowsById = new Map(sortedRows.map(row => [row.id, row]))
  uniqueRowIds.forEach(rowId => {
    const row = rowsById.get(rowId)
    if (!row) {
      throw new Error('Selected row was not found')
    }
    if (!getActiveMapping(row, witnessId)) {
      throw new Error(`${getRowLabel(row)} has no mapped unit for this witness to detach`)
    }
  })

  const detachRowIdSet = new Set(uniqueRowIds)
  const updatedRows = sortedRows.map(row => (
    detachRowIdSet.has(row.id)
      ? replaceWitnessMappings(row, witnessId, [])
      : row
  ))

  return {
    updatedRows,
    changedRowIds: uniqueRowIds,
    selectedCell: {
      rowId: uniqueRowIds[0],
      witnessId
    },
    requiresRelabel: false
  }
}

export const applyLedgerSafeDetachMapping = ({
  rows,
  witnessId,
  rowId
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
}): LedgerSafeRowEditResult => applyLedgerSafeDetachMappings({
  rows,
  witnessId,
  rowIds: [rowId]
})

export const applyLedgerSafeShiftRange = ({
  rows,
  witnessId,
  rowIds,
  direction
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowIds: string[]
  direction: 'up' | 'down'
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const selectionIndexes = getContiguousSelectionIndexes(sortedRows, rowIds)
  const firstIndex = selectionIndexes[0]
  const lastIndex = selectionIndexes[selectionIndexes.length - 1]
  const targetEdgeIndex = direction === 'up' ? firstIndex - 1 : lastIndex + 1
  if (targetEdgeIndex < 0 || targetEdgeIndex >= sortedRows.length) {
    throw new Error(`Cannot shift ${direction} from the edge row`)
  }
  const targetStartIndex = direction === 'down' ? firstIndex + 1 : firstIndex - 1
  try {
    return applyLedgerSafeRelocateRange({
      rows: sortedRows,
      witnessId,
      sourceRowIds: selectionIndexes.map(index => sortedRows[index].id),
      targetStartRowId: sortedRows[targetStartIndex].id
    })
  } catch (error) {
    if (
      error instanceof Error
      && error.message === 'Relocation requires mapped units in every selected row'
    ) {
      throw new Error('Range shift requires mapped units in every selected row')
    }
    throw error
  }
}

export const applyLedgerSafeUnmarkTransposition = ({
  rows,
  witnessId,
  rowId
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const targetRow = sortedRows.find(row => row.id === rowId)
  if (!targetRow) {
    throw new Error('Selected row was not found')
  }

  const activeMapping = getActiveMapping(targetRow, witnessId)
  if (!activeMapping) {
    throw new Error('Selected cell has no mapped unit to unmark')
  }
  if (activeMapping.translationStatus !== 'transposed') {
    throw new Error('Selected unit is not marked as transposed')
  }

  // Removing a transposition withdraws its scholarly placement. The witness
  // unit remains in segmentation and therefore reappears through the canonical
  // unassigned-fragment projection; no mutable row identity is restored.
  return applyLedgerSafeDetachMapping({
    rows: sortedRows,
    witnessId,
    rowId: targetRow.id
  })
}

export const applyLedgerSafeInsertRowAndMove = ({
  rows,
  witnessId,
  rowId,
  placement
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
  placement: 'above' | 'below'
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const sourceIndex = sortedRows.findIndex(row => row.id === rowId)
  if (sourceIndex < 0) {
    throw new Error('Selected row was not found')
  }
  const sourceRow = sortedRows[sourceIndex]
  const sourceMapping = getActiveMapping(sourceRow, witnessId)
  if (!sourceMapping) {
    throw new Error('Selected cell has no mapped unit to extract')
  }

  const newRowId = generateRowId()
  const newRow: AnchoredRow = {
    id: newRowId,
    chapterId: sourceRow.chapterId,
    labelPrefix: sourceRow.labelPrefix,
    labelNumber: sourceRow.labelNumber,
    position: computeInsertedPosition(sortedRows, sourceIndex, placement),
    unitMappings: [{
      ...sourceMapping,
      excluded: false,
      addedAt: sourceMapping.addedAt || nowIso()
    }],
    createdAt: nowIso(),
    updatedAt: nowIso(),
    status: 'needs-review'
  }

  const updatedRows = sortRows([
    ...sortedRows.map(row => {
      if (row.id !== sourceRow.id) {
        return row
      }
      return replaceWitnessMappings(row, witnessId, [])
    }),
    newRow
  ])

  return {
    updatedRows,
    changedRowIds: [sourceRow.id, newRowId],
    selectedCell: {
      rowId: newRowId,
      witnessId
    },
    requiresRelabel: true
  }
}

export const applyLedgerSafePlaceFragmentInRow = ({
  rows,
  fragment,
  rowId
}: {
  rows: AnchoredRow[]
  fragment: LedgerSafeUnassignedFragment
  rowId: string
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const targetRow = sortedRows.find(row => row.id === rowId)
  if (!targetRow) {
    throw new Error('Target row was not found')
  }
  if (getActiveMapping(targetRow, fragment.witnessId)) {
    throw new Error('Target row already has a mapped unit for this witness')
  }

  const updatedRows = sortedRows.map(row => {
    if (row.id !== rowId) {
      return row
    }
    return replaceWitnessMappings(row, fragment.witnessId, [buildMappingFromFragment(fragment)])
  })

  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: {
      rowId,
      witnessId: fragment.witnessId
    },
    requiresRelabel: false
  }
}

export const applyLedgerSafeInsertRowForFragment = ({
  rows,
  fragment,
  anchorRowId,
  placement
}: {
  rows: AnchoredRow[]
  fragment: LedgerSafeUnassignedFragment
  anchorRowId: string
  placement: 'above' | 'below'
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const anchorIndex = sortedRows.findIndex(row => row.id === anchorRowId)
  if (anchorIndex < 0) {
    throw new Error('Anchor row was not found')
  }

  const anchorRow = sortedRows[anchorIndex]
  const newRowId = generateRowId()
  const newRow: AnchoredRow = {
    id: newRowId,
    chapterId: anchorRow.chapterId,
    labelPrefix: anchorRow.labelPrefix,
    labelNumber: anchorRow.labelNumber,
    position: computeInsertedPosition(sortedRows, anchorIndex, placement),
    unitMappings: [buildMappingFromFragment(fragment)],
    createdAt: nowIso(),
    updatedAt: nowIso(),
    status: 'needs-review'
  }

  return {
    updatedRows: sortRows([...sortedRows, newRow]),
    changedRowIds: [newRowId],
    selectedCell: {
      rowId: newRowId,
      witnessId: fragment.witnessId
    },
    requiresRelabel: true
  }
}

export const applyLedgerSafeAutoPlaceFragments = ({
  rows,
  fragments
}: {
  rows: AnchoredRow[]
  fragments: LedgerSafeUnassignedFragment[]
}): LedgerSafeFragmentAutoPlacementResult => {
  const sortedFragments = [...fragments].sort((left, right) => {
    if (left.witnessId !== right.witnessId) {
      return left.witnessId.localeCompare(right.witnessId)
    }
    if (left.start !== right.start) {
      return left.start - right.start
    }
    if (left.end !== right.end) {
      return left.end - right.end
    }
    return left.unitUuid.localeCompare(right.unitUuid)
  })

  if (sortedFragments.length === 0) {
    throw new Error('No unassigned fragments are available to place')
  }

  let nextRows = sortRows(rows)
  const changedRowIds = new Set<string>()
  const placedFragments: LedgerSafeFragmentAutoPlacementAction[] = []
  const skippedFragments: LedgerSafeFragmentAutoPlacementSkip[] = []
  const insertionAnchorByBoundary = new Map<string, string>()
  let selectedCell: LedgerSafeSelectedCell | null = null
  let requiresRelabel = false

  const boundaryKeyForFragment = (fragment: LedgerSafeUnassignedFragment): string =>
    `${fragment.witnessId}:${fragment.previousRowId || 'START'}:${fragment.nextRowId || 'END'}`

  const findRow = (rowId: string | null): AnchoredRow | null =>
    rowId ? (nextRows.find(row => row.id === rowId) || null) : null

  sortedFragments.forEach(fragment => {
    const boundaryKey = boundaryKeyForFragment(fragment)
    const previousRow = findRow(fragment.previousRowId)
    const nextRow = findRow(fragment.nextRowId)
    const previousState = previousRow ? getLedgerSafeCellState(previousRow, fragment.witnessId) : null
    const nextState = nextRow ? getLedgerSafeCellState(nextRow, fragment.witnessId) : null

    const placeInRow = (rowId: string, mode: 'place-in-previous' | 'place-in-next'): boolean => {
      try {
        const result = applyLedgerSafePlaceFragmentInRow({
          rows: nextRows,
          fragment,
          rowId
        })
        nextRows = result.updatedRows
        result.changedRowIds.forEach(changedRowId => changedRowIds.add(changedRowId))
        selectedCell = result.selectedCell
        placedFragments.push({
          fragmentUnitUuid: fragment.unitUuid,
          mode,
          rowId,
          changedRowIds: result.changedRowIds
        })
        insertionAnchorByBoundary.set(boundaryKey, rowId)
        return true
      } catch {
        return false
      }
    }

    if (previousRow && previousState !== 'mapped' && placeInRow(previousRow.id, 'place-in-previous')) {
      return
    }
    if (nextRow && nextState !== 'mapped' && placeInRow(nextRow.id, 'place-in-next')) {
      return
    }

    const chainedAnchorRowId = insertionAnchorByBoundary.get(boundaryKey)
    const chainedAnchorRow = findRow(chainedAnchorRowId || null)
    const anchorRowId = chainedAnchorRow?.id || previousRow?.id || nextRow?.id || null
    if (!anchorRowId) {
      skippedFragments.push({
        fragmentUnitUuid: fragment.unitUuid,
        reason: 'No neighboring collation row is available for placement'
      })
      return
    }

    const placement: 'above' | 'below' = chainedAnchorRow || previousRow ? 'below' : 'above'
    try {
      const result = applyLedgerSafeInsertRowForFragment({
        rows: nextRows,
        fragment,
        anchorRowId,
        placement
      })
      nextRows = result.updatedRows
      result.changedRowIds.forEach(changedRowId => changedRowIds.add(changedRowId))
      selectedCell = result.selectedCell
      requiresRelabel = true
      placedFragments.push({
        fragmentUnitUuid: fragment.unitUuid,
        mode: placement === 'below' ? 'insert-after-previous' : 'insert-before-next',
        anchorRowId,
        rowId: result.selectedCell.rowId,
        changedRowIds: result.changedRowIds
      })
      insertionAnchorByBoundary.set(boundaryKey, result.selectedCell.rowId)
    } catch (error) {
      skippedFragments.push({
        fragmentUnitUuid: fragment.unitUuid,
        reason: error instanceof Error ? error.message : 'Fragment placement failed'
      })
    }
  })

  if (!selectedCell || placedFragments.length === 0) {
    throw new Error(skippedFragments[0]?.reason || 'No fragments could be placed')
  }

  return {
    updatedRows: nextRows,
    changedRowIds: Array.from(changedRowIds),
    selectedCell,
    requiresRelabel,
    placedFragments,
    skippedFragments
  }
}

export const applyLedgerSafeMarkGap = ({
  rows,
  witnessId,
  rowId
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const row = sortedRows.find(candidate => candidate.id === rowId)
  if (!row) {
    throw new Error('Selected row was not found')
  }
  if (getActiveMapping(row, witnessId)) {
    throw new Error('Move the mapped unit before marking this row as a gap')
  }
  if (getExcludedMapping(row, witnessId)) {
    throw new Error('Selected row is already marked as a deliberate gap')
  }

  const gapMapping: UnitMapping = {
    witnessId,
    unitUuid: `gap:${rowId}:${witnessId}`,
    unitLabel: 'GAP',
    textSnapshot: '',
    excluded: true,
    addedAt: nowIso()
  }

  const updatedRows = sortedRows.map(candidate => {
    if (candidate.id !== rowId) {
      return candidate
    }
    return replaceWitnessMappings(candidate, witnessId, [gapMapping])
  })

  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: { rowId, witnessId },
    requiresRelabel: false
  }
}

export const applyLedgerSafeClearGap = ({
  rows,
  witnessId,
  rowId
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
}): LedgerSafeRowEditResult => {
  const sortedRows = sortRows(rows)
  const row = sortedRows.find(candidate => candidate.id === rowId)
  if (!row) {
    throw new Error('Selected row was not found')
  }
  if (!getExcludedMapping(row, witnessId)) {
    throw new Error('Selected row does not have a deliberate gap to clear')
  }

  const updatedRows = sortedRows.map(candidate => {
    if (candidate.id !== rowId) {
      return candidate
    }
    return replaceWitnessMappings(candidate, witnessId, [])
  })

  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: { rowId, witnessId },
    requiresRelabel: false
  }
}
